import {
  MinimumTrapInputSchema,
  type MinimumTrapOutput,
  type MinimumTrapRawInput,
  PayoffInputSchema,
  type PayoffOutput,
  type PayoffRawInput,
  type RepaymentStrategy,
} from "./types.js";

// ---------------------------------------------------------------------------
// The amortisation engine + the two product surfaces. Pure: same inputs always
// yield the same output; no I/O, no Date.now() (the start date is a parameter).
// ---------------------------------------------------------------------------

/**
 * Hard-rule guard (panel lens 2/3, both CRITICALs, made binding). A credit card
 * is a credit product under the NCCP Act; routing a PERSONALISED payoff result
 * to a paid credit-product handoff — balance-transfer card, personal loan, debt
 * consolidation, refinance — is the NCCP "credit assistance" kill-line, with no
 * carve-out, on a financially-stressed cohort. This guard runs over every
 * result's serialised form and THROWS if any credit-product / debt-consolidation
 * / advice term leaks in. Tested in calculate.test.ts.
 *
 * NOTE: "credit card" itself is NOT forbidden — this tool is legitimately about
 * the user's own credit card. The line is crossed only by handing off to ANOTHER
 * credit product or giving advice ("you should", "save $X"). The forbidden list
 * is therefore the downstream-product / advice vocabulary, not the subject.
 */
const FORBIDDEN_CREDIT_HANDOFF_TERMS = [
  "balance transfer",
  "balance-transfer",
  "consolidat", // consolidate / consolidation / debt consolidation
  "personal loan",
  "refinanc", // refinance / refinancing
  "debt agreement",
  "line of credit",
  "you should",
  "we recommend",
  "apply now",
  "switch to",
  "save $",
];

export function assertNoCreditHandoff(output: unknown): void {
  const serialised = JSON.stringify(output).toLowerCase();
  for (const term of FORBIDDEN_CREDIT_HANDOFF_TERMS) {
    if (serialised.includes(term)) {
      throw new Error(
        `credit-card-payoff output leaked a forbidden credit-product/advice term: "${term}"`,
      );
    }
  }
}

/** Round to cents — interest accrues in cents; we present whole dollars in copy. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Advance an ISO date (YYYY-MM-DD) by a whole number of months. Pure date
 * arithmetic on UTC so it is deterministic regardless of host timezone. Clamps
 * to the last day of the target month (e.g. 31 Jan + 1 month → 28/29 Feb).
 */
function addMonths(isoDate: string, months: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined) {
    throw new Error(`invalid ISO date "${isoDate}"`);
  }
  // Month is 1-based in the string, 0-based in Date — m-1 + months.
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const targetYear = target.getUTCFullYear();
  const targetMonth = target.getUTCMonth(); // 0-based
  // Last day of the target month.
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const day = Math.min(d, lastDay);
  const mm = String(targetMonth + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${targetYear}-${mm}-${dd}`;
}

/**
 * The minimum repayment for a closing balance under the regulated rule: the
 * GREATER of the dollar floor or `percent × balance`, but NEVER more than the
 * closing balance itself (ASIC MoneySmart's closing-balance cap). When the
 * balance is below the floor, the cap means you simply pay off the balance.
 */
export function minimumRepayment(
  balance: number,
  percentOfBalance: number,
  dollarFloor: number,
): number {
  const byPercent = balance * percentOfBalance;
  const greaterOf = Math.max(byPercent, dollarFloor);
  // Never more than the closing balance.
  return Math.min(greaterOf, balance);
}

/** The repayment due this month for the chosen strategy, given the balance
 * AFTER interest has been added (the "closing balance" the minimum is a % of). */
function paymentForMonth(
  strategy: RepaymentStrategy,
  closingBalance: number,
  defaults: { percentOfBalance: number; dollarFloor: number },
): number {
  if (strategy.kind === "fixed") {
    // A fixed payment never overshoots the balance — pay the lesser.
    return Math.min(strategy.monthlyPayment, closingBalance);
  }
  const pct = strategy.percentOfBalance ?? defaults.percentOfBalance;
  const floor = strategy.dollarFloor ?? defaults.dollarFloor;
  return minimumRepayment(closingBalance, pct, floor);
}

/**
 * Amortise a balance month-by-month under a repayment strategy. Monthly
 * compounding: each month, interest = balance × (annualRate / 12) is added,
 * THEN the repayment is subtracted from that closing balance (a documented
 * monthly-accrual approximation of daily accrual — the standard MoneySmart
 * model). Returns months-to-clear, total interest, the payoff date, and a
 * `clears` flag that is FALSE when the payment never reduces the balance
 * (degenerate input — bounded by `maxMonths`, never an infinite loop).
 */
export function payoffSchedule(
  rawInput: PayoffRawInput,
  defaults: { percentOfBalance: number; dollarFloor: number },
): PayoffOutput {
  const input = PayoffInputSchema.parse(rawInput);
  const monthlyRate = input.annualRate / 12;

  let balance = input.balance;
  let totalInterest = 0;
  let totalPaid = 0;
  let months = 0;
  let clears = true;

  const firstMonthInterest = round2(balance * monthlyRate);

  // Already clear, or a zero/at-floor balance — clears immediately.
  while (balance > 0) {
    if (months >= input.maxMonths) {
      clears = false;
      break;
    }
    const interest = balance * monthlyRate;
    const closingBalance = balance + interest;
    const payment = paymentForMonth(input.strategy, closingBalance, defaults);

    // Degenerate input: the payment does not even cover this month's interest,
    // so the balance grows (or stalls). It will never clear — stop and flag it
    // rather than looping to maxMonths needlessly. The minimum-rule strategy
    // never hits this (its payment is a % of the post-interest balance, always
    // ≥ interest once the floor is small), but a low FIXED payment does.
    if (payment <= interest && balance + interest - payment >= balance) {
      clears = false;
      break;
    }

    balance = closingBalance - payment;
    totalInterest += interest;
    totalPaid += payment;
    months += 1;

    // Guard against floating-point dust leaving a sub-cent balance forever.
    if (balance < 0.005) {
      balance = 0;
    }
  }

  const out: PayoffOutput = {
    balance: input.balance,
    annualRate: input.annualRate,
    clears,
    monthsToClear: clears ? months : null,
    totalInterestPaid: round2(totalInterest),
    totalPaid: round2(totalPaid),
    payoffDate: clears ? addMonths(input.startDate, months) : null,
    firstMonthInterest,
  };
  // Hard-rule guard runs on every personalised result.
  assertNoCreditHandoff(out);
  return out;
}

/**
 * Contrast paying the regulated MINIMUM-ONLY against a FIXED monthly amount —
 * the ASIC MoneySmart "minimum repayment trap" framing. Returns both
 * amortisations plus the EXTRA interest and EXTRA months the minimum-only path
 * costs, as CITED ARITHMETIC — never a "save $X" claim, never a product handoff.
 */
export function minimumTrap(
  rawInput: MinimumTrapRawInput,
  defaults: { percentOfBalance: number; dollarFloor: number },
): MinimumTrapOutput {
  const input = MinimumTrapInputSchema.parse(rawInput);
  const rule = input.minimumRule ?? defaults;

  const minimumOnly = payoffSchedule(
    {
      balance: input.balance,
      annualRate: input.annualRate,
      strategy: {
        kind: "minimum",
        percentOfBalance: rule.percentOfBalance,
        dollarFloor: rule.dollarFloor,
      },
      startDate: input.startDate,
      maxMonths: input.maxMonths,
    },
    defaults,
  );

  const fixed = payoffSchedule(
    {
      balance: input.balance,
      annualRate: input.annualRate,
      strategy: { kind: "fixed", monthlyPayment: input.fixedMonthlyPayment },
      startDate: input.startDate,
      maxMonths: input.maxMonths,
    },
    defaults,
  );

  // Extra interest / months only meaningful when both paths clear. If the
  // minimum path doesn't clear within the cap, the extra is unbounded — leave
  // null and let copy say so qualitatively.
  const bothClear =
    minimumOnly.clears &&
    minimumOnly.monthsToClear !== null &&
    fixed.clears &&
    fixed.monthsToClear !== null;

  const extraInterest = bothClear
    ? round2(minimumOnly.totalInterestPaid - fixed.totalInterestPaid)
    : null;
  const extraMonths = bothClear
    ? (minimumOnly.monthsToClear as number) - (fixed.monthsToClear as number)
    : null;

  const out: MinimumTrapOutput = {
    balance: input.balance,
    annualRate: input.annualRate,
    minimumOnly,
    fixed,
    extraInterest,
    extraMonths,
    minimumInterestExceedsPrincipal:
      minimumOnly.totalInterestPaid > input.balance,
  };

  // Hard-rule guard: nothing in a personalised result may route to a credit
  // product or give advice.
  assertNoCreditHandoff(out);
  return out;
}
